<template>
  <div class="party-container">
    <h1>Party Booking</h1>

    <!-- 步骤指示器 -->
    <div class="booking-steps">
      <div class="step" :class="{ active: currentStep === 1 }">1. Personal Info</div>
      <div class="step" :class="{ active: currentStep === 2 }">2. Terms</div>
      <div class="step" :class="{ active: currentStep === 3 }">3. Package</div>
      <div class="step" :class="{ active: currentStep === 4 }">4. Capacity</div>
      <div class="step" :class="{ active: currentStep === 5 }">5. Theme</div>
      <div class="step" :class="{ active: currentStep === 6 }">6. Date</div>
      <div class="step" :class="{ active: currentStep === 7 }">7. Time</div>
      <div class="step" :class="{ active: currentStep === 8 }">8. Food</div>
      <div class="step" :class="{ active: currentStep === 9 }">9. Confirm</div>
    </div>

    <div class="booking-section">
      <!-- 步骤 1: 个人信息 -->
      <div v-if="currentStep === 1" class="step-container">
        <h2>Personal Information</h2>
        <div class="form-group">
          <label>Parent Name:</label>
          <input v-model="personalInfo.parentName" type="text" required placeholder="Enter parent's name">
        </div>
        <div class="form-group">
          <label>Email:</label>
          <input v-model="personalInfo.email" type="email" required placeholder="Enter email address">
        </div>
        <div class="form-group">
          <label>Phone Number:</label>
          <input v-model="personalInfo.phone" type="tel" required placeholder="Enter phone number">
        </div>
        <div class="form-group">
          <label>Birthday Child's Name:</label>
          <input v-model="personalInfo.childName" type="text" required placeholder="Enter child's name">
        </div>
        <div class="form-group">
          <label>Child's Turning Age:</label>
          <input v-model.number="personalInfo.childAge" type="number" min="1" max="18" required
            placeholder="Enter child's turning age">
        </div>
        <div>
          <p>
            Alternatively, if you would like to inquire in person or through our email, you will be able to access
            <a href="/assets/Booking Form 2025.pdf" target="_blank" class="pdf-link"> KiddozCafe Party Booking Form</a>.
          </p>
          <p>
            Please send us your completed form to <a href="mailto:inquiry@kiddozcafe.com.au"
              class="pdf-link">inquiry@kiddozcafe.com.au</a>
            or visit us in person and speak to our lovely staff for assisstance!
          </p>
        </div>
        <button class="next-btn" :disabled="!isPersonalInfoValid" @click="nextStep">
          Next
        </button>
      </div>

      <!-- 步骤 2: 条款确认 -->
      <div v-if="currentStep === 2" class="step-container">
        <h2>Terms & Conditions</h2>
        <div class="terms-group">
          <div class="terms-item">
            <label class="checkbox-label">
              <input type="checkbox" v-model="terms.venueTerms">
              <div class="terms-content">
                <span>I acknowledge that I have read and agree to the party and venue terms & conditions
                  outlined.</span>
                <a href="/assets/Booking Form 2025.pdf" target="_blank" class="pdf-link">View Terms & Conditions PDF</a>
              </div>
            </label>
          </div>

          <div class="terms-item">
            <label class="checkbox-label">
              <input type="checkbox" v-model="terms.packageTerms">
              <div class="terms-content">
                <span>I have read and hereby confirm the inclusions of the selected party package.</span>
                <a href="/assets/Party Themes & Food Samples.pdf" target="_blank" class="pdf-link">View Package Details
                  PDF</a>
              </div>
            </label>
          </div>

          <div class="terms-item">
            <label class="checkbox-label">
              <input type="checkbox" v-model="terms.additionalCosts">
              <div class="terms-content">
                <span>I agree that any additional requests can and may incur additional costs.</span>
                <a href="/assets/Booking Form 2025.pdf" target="_blank" class="pdf-link">View Additional Costs PDF</a>
              </div>
            </label>
          </div>
        </div>
        <div class="button-group">
          <button class="back-btn" @click="prevStep">Back</button>
          <button class="next-btn" :disabled="!areTermsAccepted" @click="nextStep">
            Next
          </button>
        </div>
      </div>

      <!-- 步骤 3: 选择套餐 -->
      <div v-if="currentStep === 3" class="step-container">
        <h2>Select Package</h2>
        <div class="package-options">
          <div v-for="pkg in packages" :key="pkg.id"
            :class="['package-option', { selected: selectedPackage === pkg.id }]" @click="selectPackage(pkg.id)">
            <h3>{{ pkg.name }}</h3>
            <div class="package-details">
              <div class="price-info">
                <p class="price-item">{{ pkg.weekdayPrice }}</p>
                <p class="price-item">{{ pkg.weekendPrice }}</p>
              </div>
              <div class="capacity-info">
                <p>Package includes up to {{ pkg.maxSelect }} Guests</p>
                <p class="max-capacity">Max Capacity: {{ pkg.maxCapacity }}</p>
              </div>
            </div>
          </div>
        </div>
        <div class="packagenote">
          <a href="/assets/Booking Form 2025.pdf" target="_blank" class="pdf-link">check the link to see more details
            about the party package</a>
          <p>*ALL Party duration is 2 hrs unless additional time has been purchased。30mins: $250 60mins: $500</p>
          <p>*Max capacity is non-negotiable due to our room & venue size, according to the health/safety regulation
            codes</p>
        </div>
        <div class="button-group">
          <button class="back-btn" @click="prevStep">Back</button>
          <button class="next-btn" :disabled="!selectedPackage" @click="nextStep">
            Next
          </button>
        </div>
      </div>

      <!-- 步骤 4: 选择人数 -->
      <div v-if="currentStep === 4" class="step-container">
        <h2>Party Capacity</h2>
        <div class="capacity-container">
          <div class="capacity-info-card">
            <div class="capacity-header">
              <h3>Selected Package: {{ getPackageName }}</h3>
            </div>
            <div class="capacity-details">
              <div class="capacity-limit">
                <p>Maximum Guests Allowed: <span class="highlight">{{ getMaxCapacity }}</span></p>
                <p class="capacity-note">* Maximum capacity includes all children and adults</p>
                <p class="capacity-note">* Please notify us about additional guests in the final notes before the party.
                  We cannot guarantee the
                  accommodation for additional guests.</p>
                <p class="capacity-note">* Max capacity is non- negotiable due to our room & venue size, according to
                  the health/safety regulation codes</p>
                <p class="capacity-note1">* Additional Guests: $20 pp</p>
              </div>
              <div class="capacity-input-group">
                <label>Number of Expected Guests:</label>
                <div class="number-input">
                  <button type="button" @click="decreasePartySize" :disabled="partySize <= 1"
                    class="number-btn">-</button>
                  <input type="number" v-model.number="partySize" :max="getMaxCapacity" min="1" class="capacity-input">
                  <button type="button" @click="increasePartySize" :disabled="partySize >= getMaxCapacity"
                    class="number-btn">+</button>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="button-group">
          <button class="back-btn" @click="prevStep">Back</button>
          <button class="next-btn" :disabled="!isPartySizeValid" @click="nextStep">
            Next
          </button>
        </div>
      </div>

      <!-- 步骤 5: 选择主题 -->
      <div v-if="currentStep === 5" class="step-container">
        <h2>Party Theme</h2>
        <div class="theme-options">
          <label v-for="theme in themes" :key="theme" class="theme-option">
            <input type="radio" v-model="selectedTheme" :value="theme">
            {{ theme }}
          </label>
        </div>
        <div class="terms-item">
          <a href="/assets/Party Themes & Food Samples.pdf" target="_blank" class="pdf-link">
            Click to check our extra party service
          </a>
        </div>
        <div class="button-group">
          <button class="back-btn" @click="prevStep">Back</button>
          <button class="next-btn" :disabled="selectedTheme.length === 0" @click="nextStep">
            Next
          </button>
        </div>
      </div>

      <!-- 步骤 6: 选择日期 -->
      <div v-if="currentStep === 6" class="step-container">
        <div class="calendar-container">
          <VDatePicker v-model="selectedDate" :disabled-dates="disabledDates" :min-date="minDate" />
        </div>
        <div>
          <p class="time-note1">*ALL booking dates/times ARE NOT finalised until confirmation email is sent.</p>
          <p class="time-note1">*For best availability, we recommend booking your party at least one week in advance.</p>
        </div>
        <div class="button-group">
          <button class="back-btn" @click="prevStep">Back</button>
          <button class="next-btn" :disabled="!selectedDate" @click="nextStep">
            Next
          </button>
        </div>
      </div>

      <!-- 步骤 7: 选择时间 -->
      <div v-if="currentStep === 7" class="step-container">
        <h2>Select Time for {{ formatDate(selectedDate) }}</h2>
        <div class="slots-grid">
          <button v-for="slot in availableTimeSlots" :key="slot.time" :class="['time-slot', {
        'unavailable': !slot.available,
        'selected': selectedTime === slot.time
      }]" :disabled="!slot.available" @click="selectTimeSlot(slot)">
            {{ slot.time }}
          </button>
        </div>
        <div class="time-notes">
          <p class="time-note">*Please mention in final notes if you need other time slot options.</p>
          <p class="time-note">*Private party time slot is only available from 4pm – 6pm.</p>
          <p class="time-note">*ALL Party duration is 2 hrs unless additional time has been purchased.</p>
          <p class="time-note1">*Additional time: 30mins: $250 60mins: $500</p>
        </div>
        <div class="button-group">
          <button class="back-btn" @click="prevStep">Back</button>
          <button class="next-btn" :disabled="!selectedTime" @click="nextStep">
            Next
          </button>
        </div>
      </div>

      <!-- 步骤 8: 选择食物 -->
      <div v-if="currentStep === 8" class="step-container">
        <h2>Party Food Menu</h2>
        <p class="menu-note">Standard Portion of Party Dish: 15 Serves</p>
        <h3>You have {{ getFoodCredits }} food credits for the {{ selectedPackage }} package.</h3>
        <h3 class="vegeterian">v: Vegetarian</h3>
        <div class="food-menu-container">
          <div class="food-menu">
            <div class="food-category">
              <h3>Fried Foods</h3>
              <div class="food-items">
                <div v-for="(item, index) in foodMenu.friedFoods" :key="'fried-' + index" class="food-item">
                  <label class="food-checkbox">
                    <input type="checkbox" v-model="item.selected">
                    <span class="food-name">{{ item.name }}</span>
                    <span class="food-price">${{ item.price }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="food-category">
              <h3>Party Bites</h3>
              <div class="food-items">
                <div v-for="(item, index) in foodMenu.partySpecial" :key="'special-' + index" class="food-item">
                  <label class="food-checkbox">
                    <input type="checkbox" v-model="item.selected">
                    <span class="food-name">{{ item.name }}</span>
                    <span class="food-price">${{ item.price }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="food-category">
              <h3>Grilled & Fried Chicken</h3>
              <div class="food-items">
                <div v-for="(item, index) in foodMenu.chicken" :key="'chicken-' + index" class="food-item">
                  <label class="food-checkbox">
                    <input type="checkbox" v-model="item.selected">
                    <span class="food-name">{{ item.name }}</span>
                    <span class="food-price">${{ item.price }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="food-category">
              <h3>Burger Sliders</h3>
              <div class="food-items">
                <div v-for="(item, index) in foodMenu.burger" :key="'burger-' + index" class="food-item">
                  <label class="food-checkbox">
                    <input type="checkbox" v-model="item.selected">
                    <span class="food-name">{{ item.name }}</span>
                    <span class="food-price">${{ item.price }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="food-category">
              <h3>Half Metre Pizza</h3>
              <div class="food-items">
                <div v-for="(item, index) in foodMenu.pizza" :key="'pizza-' + index" class="food-item">
                  <label class="food-checkbox">
                    <input type="checkbox" v-model="item.selected">
                    <span class="food-name">{{ item.name }}</span>
                    <span class="food-price">${{ item.price }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="food-category">
              <h3>Big Bowl Specials</h3>
              <div class="food-items">
                <div v-for="(item, index) in foodMenu.pasta" :key="'pasta-' + index" class="food-item">
                  <label class="food-checkbox">
                    <input type="checkbox" v-model="item.selected">
                    <span class="food-name">{{ item.name }}</span>
                    <span class="food-price">${{ item.price }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="food-category">
              <h3>Signature Platters</h3>
              <div class="food-items">
                <div v-for="(item, index) in foodMenu.platters" :key="'platter-' + index" class="food-item">
                  <label class="food-checkbox">
                    <input type="checkbox" v-model="item.selected">
                    <span class="food-name">{{ item.name }}</span>
                    <span class="food-price">${{ item.price }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="food-category">
              <h3>Jug of Drinks 3L (Free Refill)</h3>
              <div class="food-items">
                <div v-for="(item, index) in foodMenu.drinks" :key="'drink-' + index" class="food-item">
                  <label class="food-checkbox">
                    <input type="checkbox" v-model="item.selected">
                    <span class="food-name">{{ item.name }}</span>
                    <span class="food-price">${{ item.price }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div class="food-category">
              <h3>Other</h3>
              <div class="food-items">
                <div class="food-item">
                  <label class="food-checkbox">
                    <input type="checkbox" v-model="coffeeTab">
                    <span class="food-name">Coffee Tab</span>
                    <span class="food-price">POA</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="food-summary">
          <h3>Selected Food Items</h3>
          <div v-if="selectedFoodItems.length === 0" class="no-items">
            No items selected yet
          </div>
          <div v-else class="selected-items">
            <div v-for="(item, index) in selectedFoodItems" :key="'selected-' + index" class="selected-item">
              <span>{{ item.name }}</span>
              <div class="quantity-control">
                <button @click="decreaseFoodQuantity(item)" :disabled="item.quantity <= 1">-</button>
                <span>{{ item.quantity }}</span>
                <button @click="increaseFoodQuantity(item)">+</button>
              </div>
              <span>${{ item.price * item.quantity }}</span>
            </div>
            <div class="total-price">
              <strong>Total:</strong> ${{ calculateFoodTotal }}
            </div>
          </div>
        </div>
        <div>
          <p class="time-note">*We take great care in selecting our food suppliers to ensure a diverse, fresh and
            delicious menu for your enjoyment. However, please note that our
            ingredients may come from various suppliers, and while we strive to accommodate dietary restrictions, we
            cannot guarantee that all items will be
            free from traces of egg, soy, gluten, nuts, dairy or other allergens. Guests with dietary concerns should
            inquire with staff. Thank you for your
            understanding.</p>
          <p class="time-note">*Each party package includes a set number of Food Credits. If your total exceeds your
            package's Food Credit, the additional cost will need to be paid on the party day.</p>
        </div>

        <div class="food-notes">
          <p>Notes:</p>
          <textarea v-model="foodNotes" placeholder="Please include any allergies or preferences (Eg. Gluten Intolerant, Vegetarian, Seafood Allergy, etc.)

Refer to Party Terms & Conditions for information on accomodations."></textarea>
        </div>

        <div class="button-group">
          <button class="back-btn" @click="prevStep">Back</button>
          <button class="next-btn" @click="nextStep">Next</button>
        </div>
      </div>

      <!-- 步骤 9: 确认信息 -->
      <div v-if="currentStep === 9" class="step-container">
        <h2>Party Booking Confirmation</h2>
        <div class="booking-summary">
          <h3>Personal Information</h3>
          <p><strong>Parent Name:</strong> {{ personalInfo.parentName }}</p>
          <p><strong>Email:</strong> {{ personalInfo.email }}</p>
          <p><strong>Phone:</strong> {{ personalInfo.phone }}</p>
          <p><strong>Birthday Child:</strong> {{ personalInfo.childName }}</p>
          <p><strong>Turning Age:</strong> {{ personalInfo.childAge }}</p>

          <h3>Party Details</h3>
          <p><strong>Package:</strong> {{ getPackageName }}</p>
          <p><strong>Party Size:</strong> {{ partySize }}</p>
          <p><strong>Theme:</strong> {{ selectedTheme }}</p>
          <p><strong>Date:</strong> {{ formatDate(selectedDate) }}</p>
          <p><strong>Time:</strong> {{ selectedTime }}</p>

          <h3>Food Selection</h3>
          <div v-if="selectedFoodItems.length === 0" class="no-items">
            No food items selected
          </div>
          <div v-else>
            <div v-for="(item, index) in selectedFoodItems" :key="'confirm-food-' + index" class="food-summary-item">
              <span>{{ item.name }}</span>
              <div class="quantity-control">
                <button @click="decreaseFoodQuantity(item)" :disabled="item.quantity <= 1">-</button>
                <span>{{ item.quantity }}</span>
                <button @click="increaseFoodQuantity(item)">+</button>
              </div>
              <span>${{ item.price * item.quantity }}</span>
            </div>
            <p><strong>Food Total:</strong> ${{ calculateFoodTotal }}</p>
            <p v-if="foodNotes"><strong>Food Notes:</strong> {{ foodNotes }}</p>
          </div>

          <div class="food-notes">
            <p>Final Notes:</p>
            <textarea v-model="finalNotes"
              placeholder="Please include all your final notes here(e.g: additional guests, other alternative time slots etc.)"></textarea>
          </div>

          <div class="deposit-section">
            <h3>Deposit Payment Needed!</h3>
            <p>To lock-in your booking, pay ${{ calculateDeposit }} deposit within 24 hours. Or else the booking will be
              cancelled automatically.</p>
            <div class="bank-info">
              <p><strong>KiddozCafe's Bank Information:</strong></p>
              <p>Account Name: Kiddo Kove Bower Pty Ltd</p>
              <p>BSB: 062-161</p>
              <p>Account Number: 1094 0469</p>
              <p>Description: Party date and Parent's name ({{ formatShortDate(selectedDate) +
        personalInfo.parentName.split(' ')[0] }})</p>
            </div>
            <p class="screenshot">Once you make the payment, please keep the screenshot and send the screenshot to our
              email:
              inquiry@kiddozcafe.com.au</p>
            <p class="note">*If you have any questions or requests regarding the party booking, please contact us at:
              inquiry@kiddozcafe.com.au</p>
          </div>

          <div class="terms-confirmation">
            <p class="time-note1">*ALL booking dates/times ARE NOT finalised until confirmation email is sent.</p>
            <p class="time-note1">*For best availability, we recommend booking your party at least one week in advance.</p>
            <p>Confirming the booking means that I have read and agree to the party and venue terms & conditions
              outline, and I have read and hereby confirmed the inclusions of the selected party package and I agree
              that any additional requests can and may incur additional costs.</p>
          </div>
        </div>
        <div class="button-group">
          <button class="back-btn" @click="prevStep">Back</button>
          <button class="submit-btn" @click="submitBooking">Submit</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { ElMessage, ElMessageBox } from 'element-plus'
import { DatePicker as VDatePicker } from 'v-calendar'
import 'v-calendar/style.css'

export default {
  name: 'PartyView',
  components: {
    VDatePicker
  },
  setup() {
    const currentStep = ref(1)
    const personalInfo = ref({
      parentName: '',
      email: '',
      phone: '',
      childName: '',
      childAge: null
    })
    const router = useRouter()

    const terms = ref({
      venueTerms: false,
      packageTerms: false,
      additionalCosts: false
    })

    const packages = [
      {
        id: 'climber',
        name: 'Climber',
        maxCapacity: 17,
        maxSelect: 15,
        weekdayPrice: '$800',
        weekendPrice: ' '
      },
      {
        id: 'explorer',
        name: 'Explorer',
        maxCapacity: 17,
        maxSelect: 15,
        weekdayPrice: '$1,000',
        weekendPrice: ' '
      },
      {
        id: 'investigator',
        name: 'Investigator',
        maxCapacity: 35,
        maxSelect: 30,
        weekdayPrice: '$1,400',
        weekendPrice: ' '
      },
      {
        id: 'inventor',
        name: 'Inventor',
        maxCapacity: 35,
        maxSelect: 30,
        weekdayPrice: '$1,800',
        weekendPrice: ' '
      },
      {
        id: 'private',
        name: 'Private',
        maxCapacity: 70,
        maxSelect: 50,
        weekdayPrice: 'from $2,600',
        weekendPrice: ' '
      }
    ]

    const themes = computed(() => {
      if (['climber', 'investigator'].includes(selectedPackage.value)) {
        return ['No Theme/Self-decorate'];
      }
      return [
        'Super Hero',
        'Dinosaur',
        'Baby shark',
        'Paw Patrol',
        'Bluey',
        'Super Mario',
        'Frozen',
        'Disney Princess',
        'Unicorn',
        'Mermaid',
        'Sanrio',
        'No Theme/Customized',
        'TBD'
      ];
    });

    const selectedPackage = ref(null)
    const partySize = ref(null)
    const selectedTheme = ref('')
    const selectedDate = ref(null)
    const selectedTime = ref(null)
    const finalNotes = ref('')
    const formatShortDate = (date) => {
      if (!date) return '';
      const d = new Date(date);
      return `${d.getDate()}/${d.getMonth() + 1}/${d.getFullYear().toString().substr(2, 2)}`;
    };

    // 计算属性和方法
    const isPersonalInfoValid = computed(() => {
      return personalInfo.value.parentName &&
        personalInfo.value.email &&
        personalInfo.value.phone &&
        personalInfo.value.childName &&
        personalInfo.value.childAge &&
        personalInfo.value.childAge > 0 &&
        personalInfo.value.childAge <= 18
    })

    const areTermsAccepted = computed(() => {
      return terms.value.venueTerms &&
        terms.value.packageTerms &&
        terms.value.additionalCosts
    })

    const getMaxCapacity = computed(() => {
      const pkg = packages.find(p => p.id === selectedPackage.value)
      return pkg ? pkg.maxSelect : 0
    })

    const getPackageName = computed(() => {
      const pkg = packages.find(p => p.id === selectedPackage.value)
      return pkg ? pkg.name : ''
    })

    const isPartySizeValid = computed(() => {
      return partySize.value &&
        partySize.value > 0 &&
        partySize.value <= getMaxCapacity.value
    })

    const getPackageWeekdayPrice = computed(() => {
      const pkg = packages.find(p => p.id === selectedPackage.value)
      return pkg ? pkg.weekdayPrice : ''
    })

    const getPackageWeekendPrice = computed(() => {
      const pkg = packages.find(p => p.id === selectedPackage.value)
      return pkg ? pkg.weekendPrice : ''
    })

    // 日期相关
    const today = new Date()
    const minDate = new Date(today.setHours(0, 0, 0, 0))
    const disabledDates = ref([
      {
        start: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
        end: new Date(new Date().setDate(new Date().getDate() - 1)),
      },
    ])

    // 在 setup 中添加
    const confirmedTimeSlots = ref([]);

    // 修改 availableTimeSlots 为计算属性
    const availableTimeSlots = computed(() => {
      let slots = [];
      if (selectedPackage.value === 'private') {
        slots = [
          { time: '4PM - 6PM', available: true }
        ];
      } else {
        slots = [
          { time: '10AM - 12PM', available: true },
          { time: '1PM - 3PM', available: true }
        ];
      }

      // 禁用已确认的时间段
      return slots.map(slot => ({
        ...slot,
        available: !confirmedTimeSlots.value.includes(slot.time)
      }));
    });

    // 添加获取已确认时间段的方法
    const fetchConfirmedSlots = async (date) => {
      try {
        const response = await fetch(`/api/bookings/confirmed-slots/${date}`);
        if (!response.ok) {
          throw new Error('Failed to fetch confirmed slots');
        }
        const data = await response.json();
        confirmedTimeSlots.value = data.confirmedSlots;
      } catch (error) {
        console.error('Error fetching confirmed slots:', error);
      }
    };

    // 修改日期选择的监听
    // 修改日期选择的监听
    watch(selectedDate, async (newDate) => {
      if (newDate) {
        // 使用本地时区格式化日期
        const year = newDate.getFullYear();
        const month = String(newDate.getMonth() + 1).padStart(2, '0');
        const day = String(newDate.getDate()).padStart(2, '0');
        const formattedDate = `${year}-${month}-${day}`;
        await fetchConfirmedSlots(formattedDate);
      }
    });
    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      })
    }

    const calculateDeposit = computed(() => {
      // 获取选中的套餐
      const pkg = packages.find(p => p.id === selectedPackage.value);
      if (!pkg) return '0';

      // 判断选中日期是否为周末
      // const isWeekend = selectedDate.value ?
      //   [0, 6].includes(new Date(selectedDate.value).getDay()) :
      //   false;

      // 获取价格字符串
      // let priceStr = isWeekend ? pkg.weekendPrice : pkg.weekdayPrice;
      let priceStr = pkg.weekdayPrice; // 现在周末和工作日价格相同

      // 从价格字符串中提取数字部分
      let priceMatch = priceStr.match(/\$([0-9,]+)/);
      if (!priceMatch || !priceMatch[1]) return '0';

      // 转换为数字并计算50%
      const price = parseFloat(priceMatch[1].replace(/,/g, ''));
      return Math.round(price * 0.5);
    });

    const selectPackage = (packageId) => {
      selectedPackage.value = packageId
      partySize.value = null // 重置人数
    }

    const selectTimeSlot = (slot) => {
      if (slot.available) {
        selectedTime.value = slot.time
      }
    }

    const getFoodCredits = computed(() => {
      switch (selectedPackage.value) {
        case 'climber':
        case 'explorer':
          return 200;
        case 'investigator':
        case 'inventor':
          return 400;
        case 'private':
          return 600;
        default:
          return 0;
      }
    });


    const nextStep = () => {
      currentStep.value++
    }

    const prevStep = () => {
      currentStep.value--
    }

    const submitBooking = () => {
      // 显示加载状态
      const loading = ref(true);

      try {
        // 计算价格
        const isWeekend = selectedDate.value ?
          [0, 6].includes(new Date(selectedDate.value).getDay()) :
          false;

        const pkg = packages.find(p => p.id === selectedPackage.value);
        const priceStr = isWeekend ? pkg.weekendPrice : pkg.weekdayPrice;
        // 提取价格数字部分
        const priceMatch = priceStr.match(/\$?\s*([0-9]{1,3}(?:,[0-9]{3})*(?:\.[0-9]+)?|[0-9]+(?:\.[0-9]+)?)/i);
        const packagePrice = priceMatch ? parseFloat(priceMatch[1].replace(/,/g, '')) : 0;

        // 构建预订数据
        const bookingData = {
          parentName: personalInfo.value.parentName,
          email: personalInfo.value.email,
          phone: personalInfo.value.phone,
          childName: personalInfo.value.childName,
          childAge: personalInfo.value.childAge,
          packageType: getPackageName.value,
          partySize: partySize.value,
          themes: selectedTheme.value,
          bookingDate: `${selectedDate.value.getFullYear()}-${String(selectedDate.value.getMonth() + 1).padStart(2, '0')}-${String(selectedDate.value.getDate()).padStart(2, '0')}`,
          timeSlot: selectedTime.value,
          selectedFoodItems: JSON.stringify(
            selectedFoodItems.value.map(item => ({
              name: item.name,
              price: item.price,
              quantity: item.quantity
            }))
          ),
          foodNotes: foodNotes.value,
          coffeeTab: coffeeTab.value,
          foodTotal: calculateFoodTotal.value,
          packagePrice: packagePrice,
          // additionalFoodCosts: calculateFoodTotal.value - getFoodCredits.value,
          depositAmount: calculateDeposit.value,
          finalNotes: finalNotes.value
        };

        // 发送POST请求
        fetch('/api/bookings', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(bookingData)
        })
          .then(response => {
            if (!response.ok) {
              throw new Error('Booking submission failed');
            }
            return response.json();
          })
          .then(data => {
            loading.value = false;

            // 显示成功消息，包含预订号
            ElMessage({
              type: 'success',
              message: `Booking successful! Your booking number is: ${data.bookingNumber}`,
              duration: 5000
            });

            // 显示支付信息
            ElMessageBox.alert(
              `Please pay the deposit of $${calculateDeposit.value} within 24 hours to confirm your booking.
            
            Bank Account Details:
            Account Name: Kiddo Kove Bower Pty Ltd
            BSB: 062-161
            Account Number: 1094 0469
            Description: ${formatShortDate(selectedDate.value)}${personalInfo.value.parentName.split(' ')[0]}
            
            After payment, please send the screenshot to: inquiry@kiddozcafe.com.au`,
              'Payment Information',
              {
                confirmButtonText: 'OK',
                callback: () => {
                  router.push('/');
                }
              }
            );
          })
          .catch(error => {
            loading.value = false;
            console.error('Error:', error);

            ElMessage({
              type: 'error',
              message: 'Booking submission failed, please try again later',
              duration: 5000
            });
          });
      } catch (error) {
        loading.value = false;
        console.error('Error:', error);

        ElMessage({
          type: 'error',
          message: 'Booking submission failed, please try again later',
          duration: 5000
        });
      }
    }

    const decreasePartySize = () => {
      if (partySize.value > 1) {
        partySize.value--
      }
    }

    const increasePartySize = () => {
      if (partySize.value < getMaxCapacity.value) {
        partySize.value = (partySize.value || 0) + 1
      }
    }

    /**
     * 食物菜单数据
     * 包含各类食物项目及其价格
     */
    const foodMenu = ref({
      friedFoods: [
        { name: 'Chips (v)', price: 40, selected: false, quantity: 1 },
        { name: 'Wedges (v)', price: 50, selected: false, quantity: 1 },
        { name: 'Onion Rings (v)', price: 50, selected: false, quantity: 1 },
        { name: 'Chicken Nuggets', price: 55, selected: false, quantity: 1 },
        { name: 'Sweet Potato Fries (v)', price: 55, selected: false, quantity: 1 },
        { name: 'Combination Fried Food (mix/vegetables)', price: 60, selected: false, quantity: 1 },
        { name: 'Dumplings (pork/prawn/egg)', price: 60, selected: false, quantity: 1 },
        { name: 'Combination Arancini', price: 60, selected: false, quantity: 1 },
        { name: 'Panko Prawns', price: 60, selected: false, quantity: 1 },
        { name: 'Kani Croquette', price: 60, selected: false, quantity: 1 },
        { name: 'Battered Flathead', price: 60, selected: false, quantity: 1 },
        { name: 'Salted & Pepper Squid', price: 60, selected: false, quantity: 1 }
      ],
      partySpecial: [
        { name: 'Mini Quiche', price: 60, selected: false, quantity: 1 },
        { name: 'Sausage Rolls', price: 60, selected: false, quantity: 1 },
        { name: 'Mini Hotdog', price: 60, selected: false, quantity: 1 },
        { name: 'Tomato, Basil Bocconicini Puffs (v)', price: 65, selected: false, quantity: 1 },
        { name: 'Bacon & Cheese Puffs', price: 70, selected: false, quantity: 1 },
        { name: 'Garlic Prawn Crostini', price: 70, selected: false, quantity: 1 },
        { name: 'Corn & Zucchini Fritters (v)', price: 70, selected: false, quantity: 1 },
        { name: 'Salmon Blinis', price: 70, selected: false, quantity: 1 },
        { name: 'Grape, Brie, Prosciutto Canapé', price: 70, selected: false, quantity: 1 },
      ],
      chicken: [
        { name: 'Honey Wings', price: 60, selected: false, quantity: 1 },
        { name: 'Buffalo Wings', price: 60, selected: false, quantity: 1 },
        { name: 'Orleans Wings', price: 60, selected: false, quantity: 1 },
        { name: 'Fried Chicken (with Bone)', price: 70, selected: false, quantity: 1 },
        { name: 'Fried Chicken (Boneless)', price: 80, selected: false, quantity: 1 }
      ],
      burger: [
        { name: 'Chicken Burger', price: 70, selected: false, quantity: 1 },
        { name: 'Beef Burger', price: 70, selected: false, quantity: 1 },
        { name: 'Fish Burger', price: 70, selected: false, quantity: 1 },
        { name: 'Vegetarian Burger (v)', price: 70, selected: false, quantity: 1 }
      ],
      pizza: [
        { name: 'Spinach Cheese Pizza (v)', price: 55, selected: false, quantity: 1 },
        { name: 'Margarita Pizza (v)', price: 55, selected: false, quantity: 1 },
        { name: 'Hawaiian Pizza', price: 55, selected: false, quantity: 1 },
        { name: 'Roast Vegetable Pizza (v)', price: 60, selected: false, quantity: 1 },
        { name: 'Chicken, Tomato, Spinach Pizza', price: 65, selected: false, quantity: 1 },
        { name: 'Meat Lover Pizza', price: 70, selected: false, quantity: 1 },
        { name: 'Marinara Pizza', price: 75, selected: false, quantity: 1 },
        { name: 'Tom Yum Prawn Pizza', price: 75, selected: false, quantity: 1 }
      ],
      pasta: [
        { name: 'House Salad (v)', price: 50, selected: false, quantity: 1 },
        { name: 'Thai Egg Fried Rice (v)', price: 50, selected: false, quantity: 1 },
        { name: 'Bolognese', price: 70, selected: false, quantity: 1 },
        { name: 'Boscaiola', price: 70, selected: false, quantity: 1 },
        { name: 'Roast Vegetables Pasta (v)', price: 70, selected: false, quantity: 1 },
        { name: 'Marinara Pasta', price: 75, selected: false, quantity: 1 },
        { name: 'Tom Yum Prawn Pasta', price: 75, selected: false, quantity: 1 }
      ],
      platters: [
        { name: 'Mixed Sandwich Platter', price: 60, selected: false, quantity: 1 },
        { name: 'Mixed Wrap Platter', price: 70, selected: false, quantity: 1 },
        { name: 'Sweet & Salty Kids Platter', price: 70, selected: false, quantity: 1 },
        { name: 'Fruit Platter', price: 70, selected: false, quantity: 1 },
        { name: 'Choco-Drizzed Fruit Skewers (v)', price: 70, selected: false, quantity: 1 }
      ],
      drinks: [
        { name: 'Free Still Water & Sparkling Water', price: 0, selected: false, quantity: 1 },
        { name: 'Kiddoz Blue Lemon Summer Fizz', price: 25, selected: false, quantity: 1 },
        { name: 'Kiddoz Apple Summer Fizz', price: 25, selected: false, quantity: 1 },
        { name: 'Kiddoz Lychee Summer Fizz', price: 25, selected: false, quantity: 1 },
        { name: 'Apple Juice', price: 25, selected: false, quantity: 1 },
        { name: 'Orange Juice', price: 25, selected: false, quantity: 1 }
      ]
    });

    /**
     * 咖啡选项
     */
    const coffeeTab = ref(false);

    /**
     * 食物备注
     */
    const foodNotes = ref('');

    /**
     * 计算已选择的食物项目
     */
    const selectedFoodItems = computed(() => {
      const allItems = [
        ...foodMenu.value.friedFoods,
        ...foodMenu.value.partySpecial,
        ...foodMenu.value.chicken,
        ...foodMenu.value.burger,
        ...foodMenu.value.pizza,
        ...foodMenu.value.pasta,
        ...foodMenu.value.platters,
        ...foodMenu.value.drinks
      ];

      return allItems.filter(item => item.selected);
    });

    /**
     * 计算食物总价
     */
    const calculateFoodTotal = computed(() => {
      return selectedFoodItems.value.reduce((total, item) => total + (item.price * item.quantity), 0);
    });

    const decreaseFoodQuantity = (item) => {
      if (item.quantity > 1) {
        item.quantity--;
      }
    };

    const increaseFoodQuantity = (item) => {
      item.quantity++;
    };

    return {
      currentStep,
      personalInfo,
      terms,
      packages,
      themes,
      selectedPackage,
      partySize,
      selectedTheme,
      selectedDate,
      selectedTime,
      availableTimeSlots,
      isPersonalInfoValid,
      areTermsAccepted,
      getMaxCapacity,
      getPackageName,
      isPartySizeValid,
      minDate,
      disabledDates,
      formatDate,
      selectPackage,
      selectTimeSlot,
      nextStep,
      prevStep,
      submitBooking,
      getPackageWeekdayPrice,
      getPackageWeekendPrice,
      decreasePartySize,
      increasePartySize,
      calculateDeposit,
      formatShortDate,
      foodMenu,
      coffeeTab,
      foodNotes,
      selectedFoodItems,
      calculateFoodTotal,
      getFoodCredits,
      confirmedTimeSlots,
      fetchConfirmedSlots,
      decreaseFoodQuantity,
      increaseFoodQuantity
    }
  }
}
</script>

<style scoped>
.party-container {
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  padding-top: 120px;
}

h1 {
  text-align: center;
  color: #2c3e50;
  margin-bottom: 2rem;
}

.booking-section {
  display: grid;
  /* grid-template-columns: 1fr 1fr; */
  gap: 3rem;
  background: white;
  padding: 2rem;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.calendar-container {
  width: 100%;
}

.booking-info {
  padding: 1rem;
}

.booking-info h2 {
  margin-bottom: 1.5rem;
  color: #2c3e50;
}

.time-slots {
  margin-top: 1rem;
}

.slots-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 1rem;
  margin-top: 1rem;
}

.time-slot {
  padding: 0.8rem;
  border: none;
  border-radius: 8px;
  background-color: #42b983;
  color: white;
  cursor: pointer;
  transition: all 0.3s;
}

.time-slot:hover:not(.unavailable) {
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.time-slot.unavailable {
  background-color: #e0e0e0;
  cursor: not-allowed;
  opacity: 0.7;
}

.select-prompt {
  color: #666;
  font-style: italic;
}

.booking-steps {
  display: flex;
  justify-content: center;
  margin-bottom: 2rem;
  gap: 2rem;
}

.step {
  padding: 0.5rem 1rem;
  border-radius: 20px;
  background-color: #f5f5f5;
  color: #666;
}

.step.active {
  background-color: #42b983;
  color: white;
}

.step-container {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.button-group {
  display: flex;
  justify-content: space-between;
  margin-top: 2rem;
}

.next-btn,
.back-btn,
.submit-btn {
  padding: 0.8rem 2rem;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.next-btn {
  background-color: #42b983;
  color: white;
}

.next-btn:disabled {
  background-color: #e0e0e0;
  cursor: not-allowed;
}

.back-btn {
  background-color: #f5f5f5;
  color: #666;
}

.submit-btn {
  background-color: #42b983;
  color: white;
}

.time-slot.selected {
  background-color: #2c3e50;
}

.booking-summary {
  background-color: #f9f9f9;
  padding: 2rem;
  border-radius: 8px;
  margin: 1rem 0;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
}

.form-group input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
}

.terms-group {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.terms-item {
  background-color: #f9f9f9;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.terms-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.pdf-link {
  color: #42b983;
  text-decoration: none;
  font-size: 0.9em;
  display: inline-block;
  margin-top: 0.5rem;
}

.pdf-link:hover {
  text-decoration: underline;
}

.checkbox-label {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.checkbox-label input[type="checkbox"] {
  margin-top: 0.3rem;
}

.package-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 1.5rem;
  margin-bottom: 2rem;
}

.package-option {
  padding: 1.5rem;
  border: 2px solid #ddd;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.3s;
}

.package-option:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.package-option.selected {
  border-color: #42b983;
  background-color: #f0f9f4;
}

.package-option h3 {
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.package-details {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.price-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.price-item {
  font-size: 1.1em;
  font-weight: 500;
  margin: 0;
}

.capacity-info {
  border-top: 1px solid #eee;
  padding-top: 1rem;
}

.capacity-info p {
  margin: 0.3rem 0;
}

.max-capacity {
  color: #e74c3c;
  font-weight: bold;
}

.theme-options {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 1rem;
}

.theme-option {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.capacity-container {
  max-width: 600px;
  margin: 0 auto;
}

.capacity-info-card {
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
  overflow: hidden;
}

.capacity-header {
  background-color: #42b983;
  color: white;
  padding: 1rem 1.5rem;
}

.capacity-header h3 {
  margin: 0;
  font-size: 1.2rem;
}

.capacity-details {
  padding: 1.5rem;
}

.capacity-limit {
  margin-bottom: 2rem;
  text-align: center;
}

.capacity-limit .highlight {
  font-size: 1.5rem;
  color: #42b983;
  font-weight: bold;
}

.capacity-note {
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.5rem;
  font-style: italic;
}

.capacity-note1 {
  font-size: 0.9rem;
  color: #e74c3c;
  margin-top: 0.5rem;
  font-style: italic;
}

.time-note {
  font-size: 0.9rem;
  color: #666;
  margin-top: 0.5rem;
  font-style: italic;
}

.time-note1 {
  font-size: 0.9rem;
  color: #e74c3c;
  margin-top: 0.5rem;
  font-style: italic;
}

.capacity-input-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.capacity-input-group label {
  font-weight: 500;
  color: #2c3e50;
}

.number-input {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.capacity-input {
  width: 100px;
  text-align: center;
  padding: 0.5rem;
  font-size: 1.2rem;
  border: 2px solid #ddd;
  border-radius: 8px;
  -moz-appearance: textfield;
}

.capacity-input::-webkit-outer-spin-button,
.capacity-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.number-btn {
  width: 40px;
  height: 40px;
  border: none;
  background-color: #42b983;
  color: white;
  font-size: 1.5rem;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s;
}

.number-btn:hover:not(:disabled) {
  background-color: #3aa876;
}

.number-btn:disabled {
  background-color: #ddd;
  cursor: not-allowed;
}

.packagenote p {
  font-size: 1em;
  color: #666;
}

.deposit-section {
  margin-top: 2rem;
  padding: 1.5rem;
  background-color: #f0f9f4;
  border-left: 4px solid #42b983;
  border-radius: 4px;
}

.deposit-section h3 {
  color: #e74c3c;
  margin-top: 0;
}

.bank-info {
  background-color: white;
  padding: 1rem;
  border-radius: 4px;
  margin: 1rem 0;
}

.bank-info p {
  margin: 0.5rem 0;
}

.terms-confirmation {
  margin-top: 2rem;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 4px;
  font-style: italic;
  font-size: 0.9em;
}

.note {
  color: #666;
  font-style: italic;
}

.screenshot {
  font-weight: bolder;
  color: #42b983;
  font-size: 20px;
}

@media (max-width: 768px) {
  .booking-section {
    grid-template-columns: 1fr;
  }

  .booking-steps {
    flex-direction: column;
    align-items: center;
    gap: 1rem;
  }
}

.food-summary-item {
  display: grid;
  grid-template-columns: 1fr auto auto;
  /* 保持与 selected-item 相同的布局 */
  gap: 1rem;
  align-items: center;
  padding: 0.8rem;
  background-color: #f9f9f9;
  border-radius: 4px;
  margin-bottom: 0.5rem;
}

.quantity-control {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 100px;
  /* 确保数量控制区域有固定宽度 */
  justify-content: center;
}

.quantity-control button {
  width: 24px;
  height: 24px;
  border: none;
  background-color: #42b983;
  color: white;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

.quantity-control span {
  min-width: 30px;
  text-align: center;
}

.extra-service {
  text-align: center;
  margin-top: 2rem;
  padding: 1rem;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.extra-service-link {
  color: #42b983;
  text-decoration: none;
  font-size: 1.1em;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border: 2px solid #42b983;
  border-radius: 20px;
  transition: all 0.3s;
}

.extra-service-link:hover {
  background-color: #42b983;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.extra-service-link i {
  font-size: 1.2em;
}

h2 {
  text-align: center;
  color: #2c3e50;
  margin-bottom: 2rem;
}

/* 食物菜单样式 */
.food-menu-container {
  max-height: 500px;
  overflow-y: auto;
  border: 1px solid #eee;
  border-radius: 8px;
  margin-bottom: 1.5rem;
  background-color: #fff;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.food-menu {
  padding: 1rem;
}

.menu-note {
  color: #e74c3c;
  font-weight: 500;
  text-align: center;
  margin-bottom: 1rem;
}

.food-category {
  margin-bottom: 1.5rem;
  border-bottom: 1px solid #eee;
  padding-bottom: 1rem;
}

.food-category h3 {
  background-color: #f9f9f9;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  margin-bottom: 1rem;
  color: #2c3e50;
}

.food-items {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 0.5rem;
}

.food-item {
  padding: 0.5rem;
  transition: background-color 0.2s;
  border-radius: 4px;
}

.food-item:hover {
  background-color: #f9f9f9;
}

.food-checkbox {
  display: flex;
  align-items: center;
  cursor: pointer;
  width: 100%;
}

.food-name {
  flex-grow: 1;
  margin-left: 0.5rem;
}

.food-price {
  font-weight: 500;
  color: #42b983;
}

.food-summary {
  background-color: #f9f9f9;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 1.5rem;
}

.vegeterian {
  color: #27ae60;
}

.food-summary h3 {
  margin-top: 0;
  color: #2c3e50;
  border-bottom: 1px solid #eee;
  padding-bottom: 0.5rem;
  margin-bottom: 1rem;
}

.no-items {
  font-style: italic;
  color: #666;
  text-align: center;
  padding: 1rem;
}

.selected-items {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.selected-item {
  display: grid;
  grid-template-columns: 1fr auto auto;
  /* 三列布局：名称、数量控制、价格 */
  gap: 1rem;
  align-items: center;
  padding: 0.8rem;
  background-color: white;
  border-radius: 4px;
}

.total-price {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px dashed #ddd;
  text-align: right;
  font-size: 1.1em;
}

.food-notes {
  margin-bottom: 1.5rem;
}

.food-notes textarea {
  width: 100%;
  min-height: 100px;
  padding: 0.8rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  resize: vertical;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .food-items {
    grid-template-columns: 1fr;
  }

  .food-menu-container {
    max-height: 400px;
  }
}
</style>